import { Controller } from "@hotwired/stimulus"
import { Sortable } from "sortablejs"

const TEMPLATE_KEY = "__KEY__"
const ITEM = ".sidebar-editor-item"

// The sidebar editor (Sidebar::EditorComponent): reorders entries and sections by drag (SortableJS)
// or Alt+↑/↓, hides entries in place, adds and deletes custom sections, and on submit writes the
// arrangement as one JSON field — the shape of Sidebar::Layout#to_h:
//   { v: 1, sections: [{ key, title, items: [key, …] }, …], hidden: [key, …] }
// The server keeps only keys the account may see; nothing here decides visibility.
export default class extends Controller {
  static targets = [
    "sections",
    "section",
    "list",
    "title",
    "payload",
    "status",
    "sectionTemplate",
    "cancel",
  ]
  static values = { movedMessage: String, untitled: String }

  connect() {
    // The editor needs the full-width sidebar; the collapsed rail comes back with the next page,
    // which renders it from its cookie again.
    document.querySelector(".sidebar-root.sidebar-collapsed")?.classList.remove("sidebar-collapsed")

    this.sortables = [
      Sortable.create(this.sectionsTarget, {
        handle: ".sidebar-editor-section-handle",
        draggable: ".sidebar-editor-section",
        animation: 150,
        ghostClass: "sidebar-editor-ghost",
      }),
      ...this.listTargets.map((list) => this.listSortable(list)),
    ]
  }

  disconnect() {
    this.sortables?.forEach((sortable) => sortable.destroy())
  }

  listSortable(list) {
    return Sortable.create(list, {
      group: "sidebar-items",
      handle: ".sidebar-editor-handle",
      draggable: ITEM,
      animation: 150,
      ghostClass: "sidebar-editor-ghost",
    })
  }

  toggleHidden(event) {
    const item = event.currentTarget.closest(ITEM)
    this.setHidden(item, item.dataset.hidden !== "true")
  }

  setHidden(item, hidden) {
    item.dataset.hidden = String(hidden)
    const eye = item.querySelector(".sidebar-editor-eye")
    eye.setAttribute("aria-pressed", String(hidden))
    eye.setAttribute("aria-label", hidden ? eye.dataset.showLabel : eye.dataset.hideLabel)
    eye.querySelector("[data-eye-open]").hidden = hidden
    eye.querySelector("[data-eye-closed]").hidden = !hidden
  }

  addSection() {
    const key = `custom-${Math.random().toString(36).slice(2, 10) || "x"}`
    const html = this.sectionTemplateTarget.innerHTML.replaceAll(TEMPLATE_KEY, key)
    this.sectionsTarget.insertAdjacentHTML("beforeend", html)

    const section = this.sectionsTarget.lastElementChild
    this.sortables.push(
      this.listSortable(section.querySelector('[data-sidebar-editor-target="list"]')),
    )
    const title = section.querySelector('[data-sidebar-editor-target="title"]')
    title.focus()
    title.select()
  }

  // A deleted section's entries go back to their home section (built-in sections cannot be
  // deleted, so it is always there), never with the section.
  removeSection(event) {
    const section = event.currentTarget.closest(".sidebar-editor-section")
    section.querySelectorAll(ITEM).forEach((item) => this.homeList(item, section).append(item))
    section.remove()
  }

  homeList(item, except) {
    const home = this.sectionTargets.find(
      (section) => section !== except && section.dataset.sectionKey === item.dataset.home,
    )
    const fallback = this.sectionTargets.find((section) => section !== except)
    return (home ?? fallback).querySelector('[data-sidebar-editor-target="list"]')
  }

  // Alt+↑/↓ on an entry's handle: one place up or down, across into the neighbouring section at
  // the edges. Focus stays on the handle so the keys can be pressed repeatedly.
  moveWithKeyboard(event) {
    if (!event.altKey || !["ArrowUp", "ArrowDown"].includes(event.key)) return
    event.preventDefault()

    const item = event.currentTarget.closest(ITEM)
    const up = event.key === "ArrowUp"
    const lists = this.listTargets
    const index = lists.indexOf(item.parentElement)
    const neighbour = up ? item.previousElementSibling : item.nextElementSibling

    if (neighbour) {
      up ? neighbour.before(item) : neighbour.after(item)
    } else if (up && index > 0) {
      lists[index - 1].append(item)
    } else if (!up && index < lists.length - 1) {
      lists[index + 1].prepend(item)
    } else {
      return
    }

    event.currentTarget.focus()
    this.announce(item)
  }

  announce(item) {
    const section = item.closest(".sidebar-editor-section")
    const title = section.querySelector('[data-sidebar-editor-target="title"]')
    const name = title.value.trim() || title.placeholder || this.untitledValue
    this.statusTarget.textContent = this.movedMessageValue
      .replace("%{label}", item.dataset.label)
      .replace("%{section}", name)
  }

  confirmTitle(event) {
    event.currentTarget.blur()
  }

  // Escape leaves the editor unsaved — except inside a name field, where it only leaves the field.
  cancel(event) {
    if (event.target.matches?.("input")) {
      event.target.blur()
      return
    }
    this.cancelTarget.click()
  }

  serialize() {
    const json = JSON.stringify(this.layout())
    this.payloadTargets.forEach((input) => (input.value = json))
  }

  layout() {
    const sections = this.sectionTargets.map((section) => ({
      key: section.dataset.sectionKey,
      title: section.querySelector('[data-sidebar-editor-target="title"]').value.trim() || null,
      items: [...section.querySelectorAll(ITEM)].map((item) => item.dataset.itemKey),
    }))
    const hidden = [...this.element.querySelectorAll(`${ITEM}[data-hidden="true"]`)].map(
      (item) => item.dataset.itemKey,
    )
    return { v: 1, sections, hidden }
  }
}
